import {faSort} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import type {ButtonBaseProps} from '@mui/material/ButtonBase';
import ButtonBase from '@mui/material/ButtonBase';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import type {SxProps, Theme} from '@mui/material/styles';
import {useParsed} from '@refinedev/core';
import {usePopover} from 'minimal-shared/hooks';
import {useCallback, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';

import {useWorkspace} from '@app/store';
import {CustomPopover} from '@minimal/components/custom-popover';
import {baseAction} from '@minimal/theme';
import {useUserWorkspaces, WorkspaceList, workspaces, WorkspaceType} from '@workspaces/workspaces';

// ----------------------------------------------------------------------

export type WorkspacesPopoverProps = ButtonBaseProps;

export function WorkspacesPopover({sx, ...other}: WorkspacesPopoverProps) {
  const mediaQuery = 'sm';
  const {workspace, set: setWorkspace} = useWorkspace();
  const {userWorkspaces, loading} = useUserWorkspaces();
  const {t} = useTranslation();

  const {pathname} = useParsed();
  const navigate = useNavigate();

  useEffect(() => {
    const firstElement = pathname!.split('/')[1];
    const findElement = userWorkspaces.find((element) => element.id === firstElement) ?? workspaces[0];

    if (findElement === workspace) {
      return;
    }

    setWorkspace(findElement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, userWorkspaces, loading]);

  const {open, anchorEl, onClose, onOpen} = usePopover();

  const handleChangeWorkspace = useCallback(
    (newValue: WorkspaceType) => {
      setWorkspace(newValue);
      if (newValue.id === WorkspaceList.GLOBAL) {
        navigate(`/`);
      } else {
        navigate(`/${newValue.id}`);
      }
      onClose();
    },
    [onClose, navigate, setWorkspace],
  );

  const buttonBg: SxProps<Theme> = {
    height: 1,
    zIndex: -1,
    opacity: 0,
    content: "''",
    borderRadius: 1,
    position: 'absolute',
    visibility: 'hidden',
    bgcolor: 'action.hover',
    width: 'calc(100% + 8px)',
    transition: (theme) =>
      theme.transitions.create(['opacity', 'visibility'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
    ...(open && {
      opacity: 1,
      visibility: 'visible',
    }),
  };

  const renderButton = () => (
    <ButtonBase
      disableRipple
      onClick={onOpen}
      sx={[
        {
          width: '100%',
          py: 0.5,
          gap: {xs: 0.5, [mediaQuery]: 1},
          justifyContent: 'space-between',
          '&::before': buttonBg,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Box component="span" sx={{typography: 'subtitle2', display: {[mediaQuery]: 'inline-flex'}}}>
        {t(workspace.name)}
      </Box>
      <FontAwesomeIcon icon={faSort} color={baseAction.disabled} />
    </ButtonBase>
  );

  const renderMenuList = () => (
    <CustomPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      slotProps={{
        arrow: {placement: 'top-left'},
        paper: {sx: {mt: 0.5, ml: -1.55}},
      }}
    >
      <MenuList sx={{width: 240}}>
        {userWorkspaces.map((option) => (
          <MenuItem
            key={option.id}
            selected={option.id === workspace.id}
            onClick={() => handleChangeWorkspace(option)}
            sx={{height: 48}}
          >
            {option.logo && <Avatar alt={option.name} src={option.logo} />}
            <Box component="span" sx={{flexGrow: 1, fontWeight: 'fontWeightMedium'}}>
              {t(option.name)}
            </Box>
          </MenuItem>
        ))}
      </MenuList>
    </CustomPopover>
  );

  if (loading) return null;

  return (
    <>
      {renderButton()}
      {renderMenuList()}
    </>
  );
}
